import { DailyQuestWithProgress } from '../../models/apiTypes';

/**
 * Перевіряє, чи є квест квестом з рекламою
 */
export function isAdQuest(questId: number, objectiveType?: string): boolean {
  if (questId === 8 || questId === 41) return true;
  const ot = objectiveType?.trim().toLowerCase() ?? "";
  return ot === "watch_ad" || ot === "watch_ads" || ot.startsWith("watch_ad_");
}

/**
 * X-follow quests (208/209): the backend marks the quest is_finished the
 * moment the "I followed" action is recorded — well before a human moderator
 * actually confirms it (see DailyQuestCardXActions, which already treats
 * moderation as taking priority over isFinished for its own button). The
 * progress line must follow the same rule, or it flips to "1/1 ✅" the instant
 * moderation starts while the button right below it still says "MODERATION".
 */
export function isXQuestAwaitingModeration(isFollowQuest: boolean, isOnModeration: boolean): boolean {
  return isFollowQuest && isOnModeration;
}

/**
 * Об'єднує всі типи квестів в один масив
 */
export function getAllQuests(
  hourlyQuests?: DailyQuestWithProgress[],
  dailyQuests?: DailyQuestWithProgress[],
  marketingQuests?: DailyQuestWithProgress[],
  mainQuests?: DailyQuestWithProgress[],
  repeatableQuests?: DailyQuestWithProgress[]
): DailyQuestWithProgress[] {
  return [
    ...(hourlyQuests || []),
    ...(dailyQuests || []),
    ...(marketingQuests || []),
    ...(mainQuests || []),
    ...(repeatableQuests || []),
  ];
}

/**
 * Сортує квести у правильному порядку:
 * 1. Активні (незавершені або без забраної нагороди) — зверху
 * 2. Завершені з забраною нагородою — знизу
 * Всередині кожної групи — за пріоритетом questId
 */
export function sortQuests<T extends { questId: number; objectives?: Array<{ type?: string }>; isMarketing?: boolean; isFinished?: boolean; isRewardClaimed?: boolean | 0 }>(quests: T[]): T[] {
  return quests.sort((a, b) => {
    // 0. Завершені з забраною нагородою — завжди внизу
    const aFinished = !!(a.isFinished && a.isRewardClaimed);
    const bFinished = !!(b.isFinished && b.isRewardClaimed);
    if (aFinished !== bFinished) {
      return aFinished ? 1 : -1;
    }

    // 1. Квест ID 41 (реклама) - зверху
    if (a.questId === 41 && b.questId !== 41) return -1;
    if (a.questId !== 41 && b.questId === 41) return 1;

    // 2. Квест ID 7 (партнери) - другий
    if (a.questId === 7 && b.questId !== 7 && b.questId !== 41) return -1;
    if (a.questId !== 7 && b.questId === 7 && a.questId !== 41) return 1;

    // 3. Квест ID 6 (підписка) - третій
    if (a.questId === 6 && b.questId !== 6 && b.questId !== 7 && b.questId !== 41) return -1;
    if (a.questId !== 6 && b.questId === 6 && a.questId !== 7 && a.questId !== 41) return 1;

    // 4. Інші квести з рекламою (questId 8) - після квесту 41
    const aIsAdQuest = isAdQuest(a.questId, a.objectives?.[0]?.type);
    const bIsAdQuest = isAdQuest(b.questId, b.objectives?.[0]?.type);
    if (aIsAdQuest && !bIsAdQuest && b.questId !== 41 && b.questId !== 7 && b.questId !== 6) {
      if (b.questId !== 41 && b.questId !== 7 && b.questId !== 6) return -1;
    }
    if (!aIsAdQuest && bIsAdQuest && a.questId !== 41 && a.questId !== 7 && a.questId !== 6) {
      if (a.questId !== 41 && a.questId !== 7 && a.questId !== 6) return 1;
    }

    // 5. Маркетингові квести після спеціальних (але не квест 200)
    if (a.isMarketing && !b.isMarketing && b.questId !== 41 && b.questId !== 7 && b.questId !== 6 && b.questId !== 200) return -1;
    if (!a.isMarketing && b.isMarketing && a.questId !== 41 && a.questId !== 7 && a.questId !== 6 && a.questId !== 200) return 1;

    // 6. Квест 200 (запрошення друзів) - не має пріоритету, займає природнє місце
    if (a.questId === 200 && b.questId === 41) return 1;
    if (a.questId === 41 && b.questId === 200) return -1;

    // 7. Всередині кожної категорії сортуємо за quest_id для стабільності порядку
    return a.questId - b.questId;
  });
}
